import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  createStockLocation,
  updateStockLocation,
} from "../services/StockLocationService";
import type { StockLocation } from "../types/stock-location.types";
import {
  StockLocationType,
  StockLocationTypeLabel,
} from "../constants/stock-location.constants";
import schema from "./schema";

type Props = {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editing: StockLocation | null;
};

export function StockLocationModal({
  show,
  onClose,
  onSuccess,
  editing,
}: Props) {
  const isEdit = editing != null;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editing?.name ?? "",
      type: editing?.type ?? StockLocationType.WAREHOUSE,
      city: editing?.city ?? "",
      state: editing?.state ?? "",
    },
    validationSchema: schema,
    onSubmit: async (values) => {
      try {
        if (isEdit && editing) {
          const res = await updateStockLocation(editing.id, {
            name: values.name.trim(),
            type: values.type,
            city: values.city.trim(),
            state: values.state.trim(),
          });
          if (res.success) {
            toast.success(res.message);
            onSuccess();
            formik.resetForm();
            onClose();
          }
        } else {
          const res = await createStockLocation({
            name: values.name.trim(),
            type: values.type,
            city: values.city.trim(),
            state: values.state.trim(),
          });
          if (res.success) {
            toast.success(res.message);
            onSuccess();
            formik.resetForm();
            onClose();
          }
        }
      } catch (err: any) {
        toast.error(
          err?.response?.data?.message ??
            err?.message ??
            (isEdit
              ? "Failed to update stock location"
              : "Failed to create stock location"),
        );
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Modal isOpen={show} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        {isEdit ? "Edit stock location" : "Add stock location"}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted small mb-3">
          Names must be unique. Type distinguishes warehouse, site, and vehicle
          storage. City and state are required for the address.
        </p>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <div className="mb-3">
            <Label className="form-label" htmlFor="sl-modal-name">
              Name
            </Label>
            <Input
              id="sl-modal-name"
              name="name"
              type="text"
              className="form-control"
              placeholder="e.g. Main store, Site A, Van 1"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.name && formik.errors.name)}
            />
            {formik.touched.name && formik.errors.name ? (
              <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
            ) : null}
          </div>
          <div className="mb-3">
            <Label className="form-label" htmlFor="sl-modal-type">
              Type
            </Label>
            <Input
              id="sl-modal-type"
              name="type"
              type="select"
              className="form-select"
              value={formik.values.type}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.type && formik.errors.type)}
            >
              {(Object.values(StockLocationType) as StockLocationType[]).map(
                (t) => (
                  <option key={t} value={t}>
                    {StockLocationTypeLabel[t]}
                  </option>
                ),
              )}
            </Input>
            {formik.touched.type && formik.errors.type ? (
              <FormFeedback type="invalid">{formik.errors.type}</FormFeedback>
            ) : null}
          </div>
          <Row className="g-3 mb-3">
            <Col sm={6}>
              <Label className="form-label" htmlFor="sl-modal-city">
                City
              </Label>
              <Input
                id="sl-modal-city"
                name="city"
                type="text"
                className="form-control"
                placeholder="City"
                maxLength={100}
                value={formik.values.city}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.city && formik.errors.city)}
              />
              {formik.touched.city && formik.errors.city ? (
                <FormFeedback type="invalid">{formik.errors.city}</FormFeedback>
              ) : null}
            </Col>
            <Col sm={6}>
              <Label className="form-label" htmlFor="sl-modal-state">
                State
              </Label>
              <Input
                id="sl-modal-state"
                name="state"
                type="text"
                className="form-control"
                placeholder="State / province"
                maxLength={100}
                value={formik.values.state}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.state && formik.errors.state)}
              />
              {formik.touched.state && formik.errors.state ? (
                <FormFeedback type="invalid">
                  {formik.errors.state}
                </FormFeedback>
              ) : null}
            </Col>
          </Row>
          <div className="d-flex justify-content-end gap-2">
            <Button type="button" color="light" onClick={handleClose}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={formik.isSubmitting}>
              {formik.isSubmitting ? "Saving…" : isEdit ? "Save" : "Create"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
}
